import { feedbackcard, YesObject } from '@yes.technology/react-toolkit'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { FieldSettings } from 'shared/types'
import { downloadUrl } from 'shared/utils'
import { ConcatFields } from 'shared/utils/object/concatenateFields'
import { RelationConfigProps } from 'site/Renderer/components/ObjectView/components/types/object-view'
import usePDFProcessing from '../usePDFProcessing/usePDFProcessing'

type UseExportPDFProps = {
  reportType: 'list' | 'card'
  downloadType: 'single-pdf' | 'multiple-pdf-zip'
  yesObject?: YesObject
  layoutClassificationUuid?: string
  filteractionUuid?: string
  splitThreshold?: number
  relationConfig?: RelationConfigProps
  fieldSettings?: FieldSettings
  concatFields?: ConcatFields
  classificationUuid?: string
}

type UseExportPDFReturn = {
  exportPDFReport: () => void
  isLoading: boolean
  error: Error | null
  hasRequiredReportData: boolean
}

const useExportPDF = ({
  splitThreshold = 10,
  layoutClassificationUuid,
  filteractionUuid,
  reportType,
  downloadType,
  yesObject,
  relationConfig,
  fieldSettings,
  concatFields,
  classificationUuid
}: UseExportPDFProps): UseExportPDFReturn => {
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [finalUrl, setFinalUrl] = useState<string | null>(null)

  const {
    currentError,
    isLoading,
    allLoadsFinished,
    calculatedUrl,
    hasRequiredReportData
  } = usePDFProcessing({
    enabled: isExporting,
    splitThreshold,
    layoutClassificationUuid,
    filteractionUuid,
    reportType,
    downloadType,
    yesObject,
    relationConfig,
    fieldSettings,
    concatFields,
    classificationUuid
  })

  const { t } = useTranslation()

  const filename = downloadType === 'single-pdf' ? 'report.pdf' : 'report.zip'

  useEffect(() => {
    if (!isExporting) return

    if (currentError) {
      setIsExporting(false) // stop exporting on any error
      setError(currentError)
      setFinalUrl(null)
      return
    }

    if (!allLoadsFinished) {
      return
    }

    const noDataError = new Error(
      'Unknown error generating report. Data is missing'
    )

    setIsExporting(false)
    setError(calculatedUrl ? null : noDataError)
    setFinalUrl(calculatedUrl)
  }, [allLoadsFinished, calculatedUrl, currentError, isExporting])

  useEffect(() => {
    if (finalUrl) {
      downloadUrl({
        url: finalUrl,
        filename
      })

      feedbackcard(t('report.download-finished-title'), {
        message: t('report.download-finished-message'),
        type: 'success'
      })
    }
  }, [filename, finalUrl, t])

  useEffect(() => {
    if (error) {
      console.error({ error })

      feedbackcard(t('report.download-error-title'), {
        message: t('report.download-error-message'),
        type: 'error'
      })
    }
  }, [error, t])

  const exportPDFReport = useCallback(() => {
    setError(null)
    setFinalUrl(null)
    setIsExporting(true)
  }, [])

  return {
    exportPDFReport,
    isLoading,
    error,
    hasRequiredReportData
  }
}

export default useExportPDF
